import { GraduationCap, Lightbulb, Trophy } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Bilingual } from '../i18n/LanguageContext';

export type MissionId = 'learn' | 'compete' | 'prototype';

export interface Mission {
  id: MissionId;
  icon: LucideIcon;
  /** Big card title (uppercase EN). */
  title: Bilingual;
  /** Short label used in the navbar and Taa Bot quick actions. */
  navLabel: Bilingual;
  subtitle: Bilingual;
  description: Bilingual;
  cta: Bilingual;
  /** If set, the CTA button is a link to this URL/hash route instead of a card-select action. */
  ctaHref?: string;
  /** If set, shows a "Coming Soon" badge on the card. */
  comingSoon?: boolean;
  /** Card artwork (public/assets). */
  image: string;
  /* Theme classes (full literal strings so Tailwind can detect them) */
  text: string;
  iconTile: string;
  chip: string;
  borderHover: string;
  borderActive: string;
  glow: string;
  topGlow: string;
  button: string;
  /* Detail panel shown below the cards when this mission is selected */
  detail: {
    title: Bilingual;
    text: Bilingual;
    steps: Bilingual[];
  };
}

export const missions: Mission[] = [
  {
    id: 'learn',
    icon: GraduationCap,
    title: { en: 'LEARN', ar: 'تعلّم' },
    navLabel: { en: 'Learn', ar: 'تعلّم' },
    subtitle: { en: 'For students', ar: 'للطلاب' },
    description: {
      en: 'Start from the basics, complete missions, and unlock new skills.',
      ar: 'ابدأ من الأساسيات، أكمل المهمات، وافتح مهارات جديدة.',
    },
    cta: { en: 'Start Learning', ar: 'ابدأ التعلّم' },
    ctaHref: '/src/client/pages/dashboard.html',
    image: '/assets/robotlearn.png',
    text: 'text-cyan-300',
    iconTile: 'bg-cyan-400/10 text-cyan-300 ring-1 ring-cyan-400/30',
    chip: 'bg-cyan-400/10 text-cyan-200 ring-cyan-400/30',
    borderHover: 'hover:border-cyan-400/40',
    borderActive: 'border-cyan-400/60',
    glow: 'shadow-[0_0_70px_-20px_rgba(34,211,238,0.55)]',
    topGlow: 'from-cyan-400/15',
    button:
      'bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-[0_0_25px_-8px_rgba(56,189,248,0.8)] hover:brightness-110',
    detail: {
      title: { en: 'Your Robotics Learning Journey', ar: 'رحلتك لتعلّم الروبوتات' },
      text: {
        en: 'Follow guided lessons, interactive simulations, coding challenges, quizzes, and real-world projects.',
        ar: 'اتبع دروساً موجهة، ومحاكاة تفاعلية، وتحديات برمجية، واختبارات، ومشاريع من العالم الحقيقي.',
      },
      steps: [
        { en: 'Watch', ar: 'شاهد' },
        { en: 'Read', ar: 'اقرأ' },
        { en: 'Simulate', ar: 'حاكِ' },
        { en: 'Code', ar: 'برمج' },
        { en: 'Quiz', ar: 'اختبر' },
        { en: 'Build', ar: 'ابنِ' },
      ],
    },
  },
  {
    id: 'compete',
    icon: Trophy,
    title: { en: 'COMPETE', ar: 'نافس' },
    navLabel: { en: 'Compete', ar: 'نافس' },
    subtitle: { en: 'For teams', ar: 'للفرق' },
    description: {
      en: 'Train, test strategies, and enter robotics challenges.',
      ar: 'تدرّب، اختبر استراتيجياتك، وشارك في تحديات الروبوتات.',
    },
    cta: { en: 'Enter Arena', ar: 'ادخل الساحة' },
    ctaHref: '#/compete',
    image: '/assets/competition arena.png',
    text: 'text-amber-300',
    iconTile: 'bg-amber-400/10 text-amber-300 ring-1 ring-amber-400/30',
    chip: 'bg-amber-400/10 text-amber-200 ring-amber-400/30',
    borderHover: 'hover:border-amber-400/40',
    borderActive: 'border-amber-400/60',
    glow: 'shadow-[0_0_70px_-20px_rgba(251,191,36,0.55)]',
    topGlow: 'from-amber-400/15',
    button:
      'bg-gradient-to-r from-amber-500 to-orange-600 text-white shadow-[0_0_25px_-8px_rgba(251,146,60,0.8)] hover:brightness-110',
    detail: {
      title: { en: 'Enter the Robotics Arena', ar: 'ادخل ساحة الروبوتات' },
      text: {
        en: 'Prepare your robot for line follower, maze solver, sumo, and smart city competitions.',
        ar: 'جهّز روبوتك لمسابقات تتبّع الخط، وحلّ المتاهة، والسومو، والمدينة الذكية.',
      },
      steps: [
        { en: 'Choose Challenge', ar: 'اختر التحدي' },
        { en: 'Build Robot', ar: 'ابنِ الروبوت' },
        { en: 'Test Strategy', ar: 'اختبر الاستراتيجية' },
        { en: 'Run Simulation', ar: 'شغّل المحاكاة' },
        { en: 'View Replay', ar: 'شاهد الإعادة' },
        { en: 'Submit Result', ar: 'قدّم النتيجة' },
      ],
    },
  },
  {
    id: 'prototype',
    icon: Lightbulb,
    title: { en: 'PROTOTYPE', ar: 'ابتكر' },
    navLabel: { en: 'Prototype', ar: 'ابتكر' },
    subtitle: { en: 'For innovators', ar: 'للمبتكرين' },
    description: {
      en: 'Transform ideas into real hardware prototypes.',
      ar: 'حوّل أفكارك إلى نماذج أولية حقيقية.',
    },
    cta: { en: 'Build Prototype', ar: 'ابنِ نموذجك' },
    comingSoon: true,
    image: '/assets/Prototype.png',
    text: 'text-violet-300',
    iconTile: 'bg-violet-400/10 text-violet-300 ring-1 ring-violet-400/30',
    chip: 'bg-violet-400/10 text-violet-200 ring-violet-400/30',
    borderHover: 'hover:border-violet-400/40',
    borderActive: 'border-violet-400/60',
    glow: 'shadow-[0_0_70px_-20px_rgba(167,139,250,0.55)]',
    topGlow: 'from-violet-400/15',
    button:
      'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-[0_0_25px_-8px_rgba(167,139,250,0.8)] hover:brightness-110',
    detail: {
      title: { en: 'From Idea to Hardware Prototype', ar: 'من الفكرة إلى النموذج الأولي' },
      text: {
        en: 'Describe your idea, choose components, build circuits, simulate behavior, and export a prototype plan.',
        ar: 'صف فكرتك، اختر المكونات، ابنِ الدوائر، حاكِ السلوك، وصدّر خطة النموذج الأولي.',
      },
      steps: [
        { en: 'Describe Idea', ar: 'صف الفكرة' },
        { en: 'Select Components', ar: 'اختر المكونات' },
        { en: 'Build Circuit', ar: 'ابنِ الدائرة' },
        { en: 'Add Code', ar: 'أضف الكود' },
        { en: 'Run Simulation', ar: 'شغّل المحاكاة' },
        { en: 'Export Plan', ar: 'صدّر الخطة' },
      ],
    },
  },
];
